import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { callFunction } from '@/lib/api';
import { env } from '@/lib/env';
import {
  parseCareCommunicationBundle,
  type ParsedBundleContent,
} from '@/lib/medcom/parseCareCommunicationBundle';
import {
  readAckTimeoutExhausted,
  readMessageResend,
  readMessageTransmissions,
  type MessageResend,
  type MessageTransmission,
} from '@/pages/medcom/acknowledgementDisplay';
import {
  archivedMessageIdSet,
  isMissingArchiveTable,
  withoutArchivedMessages,
} from '@/lib/medcom/archivedMessages';
import { dropArchivedThreadSegments } from '@/lib/medcom/threadDisplay';
import {
  readCareCommunicationPriority,
  type CareCommunicationPriority,
} from '@/lib/medcom/careCommunicationPriority';
import { supabase } from '@/lib/supabase';

export type { CareCommunicationPriority };

export type { ParsedBundleContent };

// ── Types (mirror Communicate MessageSummary / GetThreadOutput) ───────────────

export type CareCommunicationDirection = 'in' | 'out';

export type CareCommunicationStatus =
  | 'received'
  | 'validated'
  | 'queued'
  | 'sent'
  | 'delivered'
  | 'acknowledged'
  | 'failed'
  | 'rejected';

export type CareCommunicationAckType = 'AA' | 'AE' | 'AR';

export type CareCommunicationCategory =
  | 'alcohol-and-drug-treatment'
  | 'assistive-devices'
  | 'carecoordination'
  | 'decease'
  | 'discharge'
  | 'examination-results'
  | 'healthcare'
  | 'home-care-assessment'
  | 'medicine'
  | 'nursing'
  | 'outpatient'
  | 'psychiatry-social-disability'
  | 'regarding-referral'
  | 'telemedicine'
  | 'training'
  | 'acute-ambulant'
  | 'extended-care-responsibility'
  | 'other';

export const CARE_COMMUNICATION_CATEGORIES: CareCommunicationCategory[] = [
  'alcohol-and-drug-treatment',
  'assistive-devices',
  'carecoordination',
  'decease',
  'discharge',
  'examination-results',
  'healthcare',
  'home-care-assessment',
  'medicine',
  'nursing',
  'outpatient',
  'psychiatry-social-disability',
  'regarding-referral',
  'telemedicine',
  'training',
  'acute-ambulant',
  'extended-care-responsibility',
  'other',
];

export const CARE_COMMUNICATION_STATUSES: Array<CareCommunicationStatus | 'all'> = [
  'all',
  'received',
  'validated',
  'queued',
  'sent',
  'delivered',
  'acknowledged',
  'failed',
  'rejected',
];

export const CARE_COMMUNICATION_DIRECTIONS: Array<CareCommunicationDirection | 'all'> = [
  'all',
  'in',
  'out',
];

/** Common MedCom SKS administrative role codes for author signature. */
export const AUTHOR_ROLE_CODES = [
  'psykolog',
  'speciallaege',
  'sygeplejerske',
  'fysioterapeut',
  'ergoterapeut',
  'socialraadgiver',
  'laegesekretaer',
] as const;

export interface AckSummary {
  id: string;
  ackType: CareCommunicationAckType;
  status: string;
  bundleId: string | null;
  errorCode: string | null;
  errorMessage: string | null;
  createdAt: string;
}

export interface CareCommunicationListItem {
  id: string;
  threadId: string | null;
  direction: CareCommunicationDirection;
  status: CareCommunicationStatus;
  communicationIdentifier: string;
  bundleId: string | null;
  senderSor: string | null;
  senderOrganization: string | null;
  activityCode: string | null;
  receiverSor: string | null;
  receiverOrganization: string | null;
  sentAt: string | null;
  receivedAt: string | null;
  createdAt: string;
  updatedAt: string;
  latestAcknowledgement: AckSummary | null;
  acknowledgementCount: number;
  /** Communication.topic.text. An id is dropped. */
  topic?: string | null;
  category?: string | null;
  /** Name from the letter. Never a CPR. */
  patientName?: string | null;
  /** True when patient identity is present; NEVER expose CPR value. */
  has_cpr?: boolean;
  /** `resend` when this row is Communicate's automatic resend of another letter. */
  sendType: string | null;
  /** Original message id, set on an automatic resend row. */
  parentMessageId: string | null;
  /** MessageHeader.id, when Communicate sends it. Used to hide a failed send's segments. */
  messageHeaderId?: string | null;
  /** Set on the original letter once Communicate has resent it. */
  resend: MessageResend | null;
  /** Missing-ack send attempts. Absent when Communicate has not written the field. */
  transmissions?: MessageTransmission[];
  /** True after the missing-ack resend budget is used up. Absent when not written. */
  ackTimeoutExhausted?: boolean;
  /** FHIR Communication.priority when Communicate or the letter bundle sends it. */
  priority?: CareCommunicationPriority | null;
}

export interface ThreadParty {
  organizationId?: string;
  sorId?: string;
  organizationName?: string;
  gln?: string;
  practitionerName?: string;
  practitionerRoleCode?: string;
  practitionerRoleText?: string;
  phone?: string;
}

export interface ThreadSegment {
  segmentId: string;
  timestamp: string;
  kind: 'string' | 'attachment';
  text?: string;
  attachment?: {
    contentType?: string;
    title?: string;
    size?: number;
    url?: string;
    hash?: string;
    creation?: string;
  };
  sender: ThreadParty;
  recipient: ThreadParty;
  activityCode?: string;
  messageHeaderId?: string;
  /** Owning message, when Communicate sends it. */
  messageId?: string;
  isInitiator: boolean;
}

export interface AssembledThread {
  threadId: string;
  categoryCode?: string;
  topic?: string;
  /** Reply and forward copy these identifiers into the form. The thread badge only shows that one exists. */
  patient?: {
    cpr?: string;
    xEcpr?: string;
    dEcpr?: string;
    family?: string;
    given?: string[];
  };
  initiatorOrganizationId?: string;
  segments: ThreadSegment[];
  latestMessageHeaderId?: string;
}

export interface ThreadMeta {
  id: string;
  communicationIdentifier: string;
  subject: string | null;
  status: string;
  createdAt: string;
  updatedAt: string;
}

export interface CareCommunicationThread {
  communicationIdentifier: string;
  thread: ThreadMeta | null;
  assembled: AssembledThread | null;
  messages: CareCommunicationListItem[];
}

export interface CareCommunicationListFilters {
  status?: CareCommunicationStatus | 'all';
  direction?: CareCommunicationDirection | 'all';
  search?: string;
}

export interface CareCommunicationListResult {
  items: CareCommunicationListItem[];
  pagination: {
    limit: number;
    offset: number;
    total: number;
  };
}

export interface ClinicCommunicateConnection {
  clinic_id: string;
  sor_id: string | null;
  ean: string | null;
  status: string;
  medical_speciality_code: string | null;
}

export interface ClinicSenderIdentityRow {
  clinic_id: string;
  sor: string;
  ean: string;
  name: string;
}

export type SendMessageType = 'new' | 'reply' | 'forward';

export interface OrganizationPartyInput {
  sorId: string;
  name: string;
  gln?: string;
}

export interface PatientInput {
  cpr?: string;
  xEcpr?: string;
  dEcpr?: string;
  family: string;
  given?: string[];
}

export interface AuthorSignatureInput {
  family: string;
  given: string[];
  roleCode: string;
  phone: string;
}

/** authoredAt → payload extension:date (medcom-core-datetime-extension), not Attachment.creation. */
export type PayloadSegmentInput =
  | { kind: 'string'; content: string; authoredAt?: string }
  | {
      kind: 'attachment';
      identifier?: string;
      attachment: {
        contentType: string;
        data?: string;
        url?: string;
        title?: string;
        creation?: string;
      };
      authoredAt?: string;
    };

/** Body for communicate-send (clinicId + CareCommunication compose payload). */
export interface SendCareCommunicationInput extends Record<string, unknown> {
  clinicId: string;
  type: SendMessageType;
  parent?: {
    messageId?: string;
    communicationIdentifier?: string;
  };
  sender: OrganizationPartyInput;
  receiver: OrganizationPartyInput;
  patient: PatientInput;
  category: CareCommunicationCategory;
  topic?: string;
  priority?: 'routine' | 'asap';
  author: AuthorSignatureInput;
  payloads: PayloadSegmentInput[];
  /** Forward only: identifiers of the parent letter's parts to carry. Omit for the whole letter. */
  forwardSegmentIds?: string[];
  /** Spor B: lab reports cited on this send. Refused-consent ids are rejected server-side. */
  labReportIds?: string[];
  senderCareTeamName?: string;
  recipient?: {
    family: string;
    given: string[];
    roleText?: string;
    roleCode?: string;
  };
  /**
   * Svar to a CareTeam sender: Communication.recipient → CareTeam (MedCom 3.3.4.4).
   * Never combined with `recipient`. Communicate drops it until it supports the field.
   */
  recipientCareTeam?: { name: string };
  idempotencyKey?: string;
}

export interface SendCareCommunicationOutput {
  messageId: string;
  threadId: string;
  communicationIdentifier: string;
  status: string;
  transportStatus?: string;
  bundleId?: string | null;
  sendType?: string;
  parentMessageId?: string | null;
  idempotentReplay?: boolean;
}

// ── Helpers ───────────────────────────────────────────────────────────────────

function readableText(value: unknown): string | null {
  if (typeof value !== 'string') return null;
  const text = value.trim();
  if (!text) return null;
  const bare = text.replace(/^urn:uuid:/i, '');
  if (/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(bare)) {
    return null;
  }
  return text;
}

function messageHeaderIdOf(raw: Record<string, unknown>): string | null {
  const metadata =
    raw.metadata && typeof raw.metadata === 'object'
      ? (raw.metadata as Record<string, unknown>)
      : null;
  return firstWrittenId(
    raw.messageHeaderId,
    raw.letterIdentifier,
    raw.letter_identifier,
    metadata?.messageHeaderId,
    metadata?.letterIdentifier,
  );
}

function firstWrittenId(...values: unknown[]): string | null {
  for (const value of values) {
    if (typeof value === 'string' && value.trim()) return value.trim();
  }
  return null;
}

function asListItem(raw: unknown): CareCommunicationListItem | null {
  if (!raw || typeof raw !== 'object') return null;
  const r = raw as Record<string, unknown>;
  if (typeof r.id !== 'string' || typeof r.communicationIdentifier !== 'string') return null;

  const latest = r.latestAcknowledgement;
  let latestAcknowledgement: AckSummary | null = null;
  if (latest && typeof latest === 'object') {
    const a = latest as Record<string, unknown>;
    if (typeof a.id === 'string' && typeof a.ackType === 'string') {
      latestAcknowledgement = {
        id: a.id,
        ackType: a.ackType as CareCommunicationAckType,
        status: typeof a.status === 'string' ? a.status : '',
        bundleId: typeof a.bundleId === 'string' ? a.bundleId : null,
        errorCode: typeof a.errorCode === 'string' ? a.errorCode : null,
        errorMessage: typeof a.errorMessage === 'string' ? a.errorMessage : null,
        createdAt: typeof a.createdAt === 'string' ? a.createdAt : '',
      };
    }
  }

  return {
    id: r.id,
    threadId: typeof r.threadId === 'string' ? r.threadId : null,
    direction: (r.direction === 'in' || r.direction === 'out' ? r.direction : 'out'),
    status: (typeof r.status === 'string' ? r.status : 'queued') as CareCommunicationStatus,
    communicationIdentifier: r.communicationIdentifier,
    bundleId: typeof r.bundleId === 'string' ? r.bundleId : null,
    senderSor: typeof r.senderSor === 'string' ? r.senderSor : null,
    senderOrganization:
      typeof r.senderOrganization === 'string' ? r.senderOrganization : null,
    activityCode: typeof r.activityCode === 'string' ? r.activityCode : null,
    receiverSor: typeof r.receiverSor === 'string' ? r.receiverSor : null,
    receiverOrganization:
      typeof r.receiverOrganization === 'string' ? r.receiverOrganization : null,
    sentAt: typeof r.sentAt === 'string' ? r.sentAt : null,
    receivedAt: typeof r.receivedAt === 'string' ? r.receivedAt : null,
    createdAt: typeof r.createdAt === 'string' ? r.createdAt : '',
    updatedAt: typeof r.updatedAt === 'string' ? r.updatedAt : '',
    latestAcknowledgement,
    acknowledgementCount:
      typeof r.acknowledgementCount === 'number' ? r.acknowledgementCount : 0,
    topic: readableText(r.topic),
    category: readableText(r.category),
    patientName: readableText(r.patientName),
    sendType: typeof r.sendType === 'string' ? r.sendType : null,
    parentMessageId: typeof r.parentMessageId === 'string' ? r.parentMessageId : null,
    messageHeaderId: messageHeaderIdOf(r),
    resend: readMessageResend(r),
    transmissions: readMessageTransmissions(r),
    ackTimeoutExhausted: readAckTimeoutExhausted(r),
    priority: readCareCommunicationPriority(r.priority),
  };
}

/** True when assembled patient has any identity — never returns/logs the value. */
export function patientHasIdentity(
  patient: AssembledThread['patient'] | null | undefined,
): boolean {
  if (!patient) return false;
  return Boolean(patient.cpr || patient.xEcpr || patient.dEcpr);
}

// ── Query keys ────────────────────────────────────────────────────────────────

export const careCommunicationsListQueryKey = (
  clinicId: string | null,
  filters: CareCommunicationListFilters,
) => ['care-communications', 'list', clinicId, filters] as const;

export const careCommunicationThreadQueryKey = (
  clinicId: string | null,
  communicationIdentifier: string | null | undefined,
) => ['care-communications', 'thread', clinicId, communicationIdentifier] as const;

export const clinicCommunicateConnectionQueryKey = (clinicId: string | null) =>
  ['care-communications', 'connection', clinicId] as const;

export const clinicSenderIdentitiesQueryKey = (clinicId: string | null) =>
  ['care-communications', 'sender-identities', clinicId] as const;

export const archivedMedcomMessageIdsQueryKey = (clinicId: string | null) =>
  ['care-communications', 'archived', clinicId] as const;

async function fetchArchivedMedcomMessageIds(clinicId: string): Promise<Set<string>> {
  const { data, error } = await supabase
    .from('clinic_medcom_message_archives' as never)
    .select('message_id')
    .eq('clinic_id', clinicId);
  if (error) {
    if (isMissingArchiveTable(error.code)) return new Set();
    throw error;
  }
  const ids: string[] = [];
  for (const row of (data ?? []) as { message_id?: unknown }[]) {
    if (typeof row.message_id === 'string' && row.message_id.length > 0) {
      ids.push(row.message_id);
    }
  }
  return archivedMessageIdSet(ids);
}

// ── useCareCommunicationsList ─────────────────────────────────────────────────

export function useCareCommunicationsList(
  clinicId: string | null | undefined,
  filters: CareCommunicationListFilters,
) {
  return useQuery({
    queryKey: careCommunicationsListQueryKey(clinicId ?? null, filters),
    enabled: Boolean(clinicId),
    queryFn: async (): Promise<CareCommunicationListResult> => {
      if (!clinicId) {
        return { items: [], pagination: { limit: 50, offset: 0, total: 0 } };
      }

      const body: Record<string, unknown> = {
        clinicId,
        limit: 100,
        offset: 0,
      };
      if (filters.status && filters.status !== 'all') body.status = filters.status;
      if (filters.direction && filters.direction !== 'all') body.direction = filters.direction;

      const result = await callFunction<Record<string, unknown>, CareCommunicationListResult>(
        'communicate-list',
        body,
      );

      let items = (result.items ?? [])
        .map(asListItem)
        .filter((x): x is CareCommunicationListItem => x !== null);

      const archivedIds = await fetchArchivedMedcomMessageIds(clinicId);
      const beforeArchive = items.length;
      items = withoutArchivedMessages(items, archivedIds);
      const archivedDropped = beforeArchive - items.length;

      const search = filters.search?.trim().toLowerCase();
      if (search) {
        items = items.filter((item) => {
          const hay = [
            item.topic,
            item.patientName,
            item.senderOrganization,
            item.receiverOrganization,
            item.status,
          ]
            .filter(Boolean)
            .join(' ')
            .toLowerCase();
          return hay.includes(search);
        });
      }

      const pagination = result.pagination ?? {
        limit: 100,
        offset: 0,
        total: items.length,
      };
      return {
        items,
        pagination: {
          ...pagination,
          total: Math.max(0, pagination.total - archivedDropped),
        },
      };
    },
    staleTime: 15_000,
  });
}

// ── useCareCommunicationThread ────────────────────────────────────────────────

export function useCareCommunicationThread(
  clinicId: string | null | undefined,
  communicationIdentifier: string | null | undefined,
) {
  return useQuery({
    queryKey: careCommunicationThreadQueryKey(clinicId ?? null, communicationIdentifier),
    enabled: Boolean(clinicId && communicationIdentifier),
    queryFn: async (): Promise<CareCommunicationThread> => {
      if (!clinicId || !communicationIdentifier) {
        throw new Error('Missing clinic or communication identifier');
      }

      const result = await callFunction<
        Record<string, unknown>,
        {
          communicationIdentifier: string;
          thread: ThreadMeta | null;
          assembled: AssembledThread | null;
          messages: unknown[];
        }
      >('communicate-thread', {
        clinicId,
        communicationIdentifier,
      });

      const messages = (result.messages ?? [])
        .map(asListItem)
        .filter((x): x is CareCommunicationListItem => x !== null);
      const archivedIds = await fetchArchivedMedcomMessageIds(clinicId);
      const visible = withoutArchivedMessages(messages, archivedIds);
      const assembled = result.assembled;
      if (assembled && archivedIds.size > 0) {
        assembled.segments = dropArchivedThreadSegments(
          assembled.segments,
          messages,
          archivedIds,
        );
      }

      return {
        communicationIdentifier: result.communicationIdentifier,
        thread: result.thread,
        assembled,
        messages: visible,
      };
    },
    staleTime: 5_000,
  });
}

// ── useClinicCommunicateConnection ────────────────────────────────────────────

export function useClinicCommunicateConnection(clinicId: string | null | undefined) {
  return useQuery({
    queryKey: clinicCommunicateConnectionQueryKey(clinicId ?? null),
    enabled: Boolean(clinicId),
    queryFn: async (): Promise<ClinicCommunicateConnection | null> => {
      if (!clinicId) return null;
      // Table may be absent from generated types until migrations are applied.
      const { data, error } = await supabase
        .from('clinic_communicate_connections' as never)
        .select('clinic_id, sor_id, ean, status, medical_speciality_code')
        .eq('clinic_id', clinicId)
        .maybeSingle();
      if (error) throw error;
      if (!data) return null;
      const row = data as unknown as ClinicCommunicateConnection;
      return {
        clinic_id: row.clinic_id,
        sor_id: row.sor_id,
        ean: row.ean,
        status: row.status,
        medical_speciality_code: row.medical_speciality_code,
      };
    },
    staleTime: 60_000,
  });
}

export function useClinicSenderIdentities(clinicId: string | null | undefined) {
  return useQuery({
    queryKey: clinicSenderIdentitiesQueryKey(clinicId ?? null),
    enabled: Boolean(clinicId),
    queryFn: async (): Promise<ClinicSenderIdentityRow[]> => {
      if (!clinicId) return [];
      const { data, error } = await supabase
        .from('clinic_sender_identities' as never)
        .select('clinic_id, sor, ean, name')
        .eq('clinic_id', clinicId);
      if (error) throw error;
      return (data ?? []) as ClinicSenderIdentityRow[];
    },
    staleTime: 60_000,
  });
}

// ── useSendCareCommunication ──────────────────────────────────────────────────

export function useSendCareCommunication() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (
      input: SendCareCommunicationInput,
    ): Promise<SendCareCommunicationOutput> => {
      return await callFunction<SendCareCommunicationInput, SendCareCommunicationOutput>(
        'communicate-send',
        input,
      );
    },
    onSuccess: (row, vars) => {
      qc.invalidateQueries({ queryKey: ['care-communications', 'list', vars.clinicId] });
      if (row.communicationIdentifier) {
        qc.invalidateQueries({
          queryKey: careCommunicationThreadQueryKey(vars.clinicId, row.communicationIdentifier),
        });
      }
      if (vars.parent?.communicationIdentifier) {
        qc.invalidateQueries({
          queryKey: careCommunicationThreadQueryKey(
            vars.clinicId,
            vars.parent.communicationIdentifier,
          ),
        });
      }
    },
  });
}

// ── useImportCareCommunication ────────────────────────────────────────────────

export type ImportCareCommunicationKind =
  | 'care-communication'
  | 'acknowledgement'
  | 'receipt'
  | 'unpack-error'
  | 'duplicate';

export interface ImportCareCommunicationInput extends Record<string, unknown> {
  clinicId: string;
  payload: string;
}

export interface ImportCareCommunicationOutput {
  kind: ImportCareCommunicationKind;
  messageId: string | null;
  acknowledgementId: string | null;
  detail: string | null;
  patientId?: string | null;
  patientCreated?: boolean;
  patientName?: string | null;
  patientLinked?: boolean;
  patientLinkFailed?: boolean;
  acknowledgementResent?: boolean;
}

export function useArchiveMedcomImport() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: {
      clinicId: string;
      messageId: string;
      bundleId?: string | null;
    }): Promise<void> => {
      const { error } = await supabase.rpc('archive_medcom_import' as never, {
        p_clinic_id: input.clinicId,
        p_message_id: input.messageId,
        p_bundle_id: input.bundleId ?? null,
      } as never);
      if (error) throw error;
    },
    onSuccess: (_row, vars) => {
      qc.invalidateQueries({ queryKey: ['care-communications', 'list', vars.clinicId] });
      qc.invalidateQueries({ queryKey: archivedMedcomMessageIdsQueryKey(vars.clinicId) });
      qc.invalidateQueries({ queryKey: ['care-communications', 'thread', vars.clinicId] });
    },
  });
}

export function useImportCareCommunication() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (
      input: ImportCareCommunicationInput,
    ): Promise<ImportCareCommunicationOutput> => {
      return await callFunction<ImportCareCommunicationInput, ImportCareCommunicationOutput>(
        'communicate-cc-import',
        input,
      );
    },
    onSuccess: (_row, vars) => {
      qc.invalidateQueries({ queryKey: ['care-communications', 'list', vars.clinicId] });
      qc.invalidateQueries({ queryKey: ['care-communications', 'acks', vars.clinicId] });
    },
  });
}

export interface MessageAcknowledgement {
  id: string;
  ackType: CareCommunicationAckType;
  status: string;
  bundleId: string | null;
  errorCode: string | null;
  errorMessage: string | null;
  createdAt: string;
  duplicateResend: boolean;
}

export interface MessageAcknowledgements {
  acknowledgements: MessageAcknowledgement[];
  createdAcknowledgementId: string | null;
}

export function useMessageAcknowledgements(
  clinicId: string | null | undefined,
  messageId: string | null | undefined,
) {
  const qc = useQueryClient();
  return useQuery({
    queryKey: ['care-communications', 'acks', clinicId, messageId],
    enabled: Boolean(clinicId && messageId),
    queryFn: async (): Promise<MessageAcknowledgements> => {
      if (!clinicId || !messageId) throw new Error('Missing ids');
      const result = await callFunction<
        { clinicId: string; messageId: string },
        MessageAcknowledgements
      >('communicate-message-acks', { clinicId, messageId });
      if (result.createdAcknowledgementId) {
        qc.invalidateQueries({ queryKey: ['care-communications', 'list', clinicId] });
      }
      return result;
    },
    staleTime: 5_000,
  });
}

// ── useExportCareCommunication ────────────────────────────────────────────────

/**
 * Trigger a FHIR Bundle file download via communicate-export Edge Function.
 * Mirrors useExportPayTransactions (raw GET + Content-Disposition blob download).
 */
export function useExportCareCommunication() {
  return useMutation({
    mutationFn: async (args: {
      clinicId: string;
      messageId: string;
      format: 'json' | 'xml';
      acknowledgementId?: string;
      envelope?: boolean;
      reportId?: string;
    }): Promise<void> => {
      const {
        data: { session },
      } = await supabase.auth.getSession();

      const qs = new URLSearchParams({
        clinicId: args.clinicId,
        messageId: args.messageId,
        format: args.format,
      });
      if (args.envelope) {
        qs.set('envelope', '1');
      } else if (args.acknowledgementId) {
        qs.set('acknowledgementId', args.acknowledgementId);
      }
      if (args.reportId) qs.set('reportId', args.reportId);

      const resp = await fetch(
        `${env.VITE_SUPABASE_URL}/functions/v1/communicate-export?${qs.toString()}`,
        {
          method: 'GET',
          headers: {
            Authorization: `Bearer ${session?.access_token ?? ''}`,
            apikey: env.VITE_SUPABASE_ANON_KEY,
          },
        },
      );

      if (!resp.ok) {
        const json = (await resp.json().catch(() => null)) as {
          error?: { message?: string };
        } | null;
        throw new Error(json?.error?.message ?? `Export failed (${resp.status})`);
      }

      const blob = await resp.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;

      const cd = resp.headers.get('Content-Disposition') ?? '';
      const match = /filename="?([^";]+)"?/.exec(cd);
      a.download = match?.[1] ?? (
        args.envelope
          ? `${args.messageId}-envelope.xml`
          : `${args.messageId}.${args.format}`
      );

      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    },
  });
}

// ── useBundleJson ─────────────────────────────────────────────────────────────

/**
 * Fetch the JSON export for a message and parse FHIR content for display.
 */
export async function fetchParsedCareCommunicationBundle(
  clinicId: string,
  messageId: string,
): Promise<ParsedBundleContent> {
  const {
    data: { session },
  } = await supabase.auth.getSession();

  const qs = new URLSearchParams({ clinicId, messageId, format: 'json' });
  const resp = await fetch(
    `${env.VITE_SUPABASE_URL}/functions/v1/communicate-export?${qs.toString()}`,
    {
      headers: {
        Authorization: `Bearer ${session?.access_token ?? ''}`,
        apikey: env.VITE_SUPABASE_ANON_KEY,
      },
    },
  );

  if (!resp.ok) throw new Error(`Export failed (${resp.status})`);

  const bundle = (await resp.json()) as Record<string, unknown>;
  return parseCareCommunicationBundle(bundle);
}

export function useThreadLetterBundles(
  clinicId: string | null | undefined,
  messageIds: readonly string[],
) {
  const key = messageIds.join('|');
  return useQuery({
    queryKey: ['thread-letter-bundles', clinicId, key],
    enabled: Boolean(clinicId && messageIds.length > 0),
    staleTime: 60_000,
    queryFn: async (): Promise<Map<string, ParsedBundleContent | null>> => {
      if (!clinicId) return new Map();
      const entries = await Promise.all(
        messageIds.map(async (id) => {
          try {
            const letter = await fetchParsedCareCommunicationBundle(clinicId, id);
            return [id, letter] as const;
          } catch {
            return [id, null] as const;
          }
        }),
      );
      return new Map(entries);
    },
  });
}

export function useBundleJson(
  clinicId: string | null | undefined,
  messageId: string | null | undefined,
) {
  return useQuery({
    queryKey: ['bundle-json', clinicId, messageId],
    enabled: Boolean(clinicId && messageId),
    staleTime: 60_000,
    queryFn: async (): Promise<ParsedBundleContent> => {
      if (!clinicId || !messageId) throw new Error('Missing ids');
      return fetchParsedCareCommunicationBundle(clinicId, messageId);
    },
  });
}

/** Clinio patient linked to this letter, when import created the link. */
export function useLinkedMedcomLetterPatient(
  clinicId: string | null | undefined,
  messageId: string | null | undefined,
) {
  return useQuery({
    queryKey: ['patient-medcom-letter-link', clinicId, messageId] as const,
    enabled: Boolean(clinicId && messageId),
    staleTime: 60_000,
    queryFn: async (): Promise<{ patientId: string } | null> => {
      if (!clinicId || !messageId) return null;
      const { data, error } = await supabase
        .from('patient_medcom_letters' as never)
        .select('patient_id')
        .eq('clinic_id', clinicId)
        .eq('message_id', messageId)
        .maybeSingle();
      if (error) throw error;
      const row = data as { patient_id?: string } | null;
      const patientId = typeof row?.patient_id === 'string' ? row.patient_id : '';
      return patientId ? { patientId } : null;
    },
  });
}
